import { PaperClipIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useBlobUrlHolder } from 'src/hooks/useBlobUrlHolder.hook';
import { Button, Textarea } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Download from 'yet-another-react-lightbox/plugins/download';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import { parseSlides } from '../../../../Chat.functions';
import { useChatContext } from '../../../../Chat.hook';
import { useMessageBarContext } from '../../MessageBar.hook';
import AudioRecorderAction from '../ToolActions/components/AudioRecorderAction/AudioRecorderAction.component';
import DriveFilesAction from '../ToolActions/components/DriveFilesAction/DriveFilesAction.component';
import DriveFilesActionProvider from '../ToolActions/components/DriveFilesAction/DriveFilesAction.context';
import EmojiAction from '../ToolActions/components/EmojiAction/EmojiAction.component';
import { VariablesMenu } from '../VariablesMenu/VariablesMenu';
import { listVariables } from '../VariablesMenu/VariablesMenu.constants';
import { tListVariables } from '../VariablesMenu/VariablesMenu.types';
import Attachment from './components/Attachment.component';
import { tLegacyInputProps } from './LegacyInput.types';

export default function LegacyInput(props: tLegacyInputProps) {
  const {
    attachments,
    actionButtonText,
    actionButtonLoading,
    actionButtonDisabled,
    setNewAttachments,
    handleActionButton,
    cancelButtonText,
    onCancel,
  } = props;
  const attachmentUrls = useMemo(() => attachments?.map((attachment) => attachment.url) ?? [], [attachments]);
  useBlobUrlHolder(attachmentUrls);
  const { textAreaRef, selectedChat } = useChatContext();
  const {
    messageBarValue,
    setMessageBarValue,
    draftMessages,
    setDraftMessages,
  } = useMessageBarContext();
  const { t } = useTranslation('chat/chat');

  const [openDrive, setOpenDrive] = useState(false);
  const [openVariableMenu, setOpenVariableMenu] = useState<boolean>(false);
  const [dataVariables, setDataVariables] =
    useState<tListVariables[]>(listVariables);

  const [openLightbox, setOpenLightbox] = useState<boolean>(false);

  function onCloseLightbox() {
    setOpenLightbox(false);
  }

  function handleKeyUp(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    const { key, currentTarget } = e;
    const query = currentTarget.value.toLowerCase();

    switch (key) {
      case '{':
        setOpenVariableMenu(true);
        break;
      case '}':
        setOpenVariableMenu(false);
        break;
      default:
        if (query.length > 0) {
          const filtered = dataVariables.filter((variable) =>
            variable.name.toLowerCase().includes(key),
          );
          setDataVariables(filtered.length ? filtered : listVariables);
        } else {
          setDataVariables(listVariables);
          setOpenVariableMenu(false);
        }
        break;
    }
  }

  function addVariableToTextArea(item: string) {
    setOpenVariableMenu(false);
    if (textAreaRef.current) {
      const textArea = textAreaRef.current;
      // Get the current cursor position
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;

      // Get the current text value of the textarea
      const currentValue = textArea.value;

      // We split the text into two parts: before and after the cursor
      const beforeCursor = currentValue.substring(0, start);
      const afterCursor = currentValue.substring(end);

      // Insert the text between the two parts
      const newValue = `${beforeCursor}${item}}${afterCursor}`;
      setMessageBarValue(newValue);

      // Finally we keep the cursor after the inserted text
      const cursorPosition = start + `{${item}}`.length;
      textArea.setSelectionRange(cursorPosition, cursorPosition);
    }
  }

  function onChangeTextArea(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;
    const currentDraft = draftMessages?.findIndex(
      (draftMessage) => draftMessage.leadId === selectedChat?.leadId,
    );

    const draftExists = currentDraft !== undefined && currentDraft > -1;

    if (draftMessages) {
      if (draftExists) {
        draftMessages[currentDraft].message = value;
      }

      if (!draftExists) {
        draftMessages.push({
          leadId: selectedChat?.leadId || '',
          message: value,
          attachments: [],
        });
      }
    }

    setDraftMessages(draftMessages);
    setMessageBarValue(e.target.value);
  }

  return (
    <DriveFilesActionProvider>
      <div className="relative w-full form-control bg-base-100 p-2">
        {openVariableMenu && (
          <VariablesMenu
            list={dataVariables}
            onHandleClick={addVariableToTextArea}
          />
        )}

        <Textarea
          color="ghost"
          placeholder={t('legacyInput.placeholder')}
          className="border-none resize-none focus:border-none focus:outline-none focus:outline-offset-0 focus:bg-transparent"
          rows={3}
          onKeyUp={handleKeyUp}
          ref={textAreaRef}
          value={messageBarValue}
          onChange={onChangeTextArea}
        />

        {attachments ? (
          <div className="flex w-full gap-4 px-4 py-3 overflow-x-scroll overflow-y-visible">
            {attachments?.map((file, index) => (
              <Attachment
                key={index}
                file={file}
                index={index}
                setNewAttachments={setNewAttachments}
                handleOnClick={() => {
                  setOpenLightbox(true);
                }}
              />
            ))}

            {openLightbox && (
              <Lightbox
                plugins={[
                  Zoom,
                  Captions,
                  Thumbnails,
                  Download,
                  Fullscreen,
                  Video,
                ]}
                open={openLightbox}
                close={onCloseLightbox}
                slides={parseSlides(attachments)}
              />
            )}
          </div>
        ) : null}
        <div className="flex justify-end items-center gap-3">
          <Button
            shape="circle"
            size="sm"
            color="ghost"
            onClick={() => setOpenDrive(true)}
          >
            <PaperClipIcon className="ActionButtons__Button__Icon" />
          </Button>
          <Button shape="circle" size="sm" color="ghost">
            <AudioRecorderAction setNewAttachments={setNewAttachments} />
          </Button>
          <Button shape="circle" size="sm" color="ghost">
            <EmojiAction isLegacyEditor={true} />
          </Button>
          {onCancel && (
            <Button color="ghost" onClick={onCancel}>
              {cancelButtonText}
            </Button>
          )}
          <Button
            color="primary"
            disabled={actionButtonDisabled}
            loading={actionButtonLoading}
            onClick={handleActionButton}
          >
            {actionButtonText}
          </Button>
        </div>
        {openDrive && setNewAttachments && (
          <DriveFilesAction
            setNewAttachments={setNewAttachments}
            setOpenDrive={setOpenDrive}
          />
        )}
      </div>
    </DriveFilesActionProvider>
  );
}
